import { FC, memo, useCallback, useRef, useState } from 'react';
import { FormattedAlgoliaProductType } from '~/features/productsList/types';
import { Product } from '~/lib/data-contract';
import { CMSLink } from '../cms-link/cms-link.component';
import ProductImage from '../product-image/product-image.component';
import styles from './product-card.module.css';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import { gtmProductMapper, useFranchise, useGtm } from '~/shared/utils';
import { WrappedInsightsClient } from 'react-instantsearch-core';
import { useAlgoliaAnalytics, useIsInitialRenderState } from '~/shared/hooks';
import { ProductImpressionTracker } from '../_ProductCard/components/ProductImpressionTracker/ProductImpressionTracker';
import { useMedia } from 'react-use';
import { queries } from '~/theme';
import { useInView } from '~/shared/hooks/useInView/useInView';
import { PriceGroup } from '../price-group/price-group.component';
import Arrow from '$icons/arrow-up-right.svg';
import { Typography } from '../typography/typography.component';
import clsx from 'clsx';
import { ProductTagsLegacy } from '../_ProductTags';

const fallBackImageSizes = `${queries.belowMd} 50vw, ${queries.belowMd} 30vw, ${queries.belowLg} 22vw, 16vw`;

type Props = {
    product: FormattedAlgoliaProductType | Product;
    isSlider?: boolean;
    queryId?: string;
    listType: string;
    index?: number;
    isProductListPage?: boolean;
    sizes?: string;
    insights?: WrappedInsightsClient;
};

export const ProductCard: FC<Props> = memo(
    ({ product, isSlider = false, listType, insights, index, queryId, sizes }) => {
        const isMobile = !useMedia(queries.md, false);
        const [activeSlide, setActiveSlide] = useState(0);
        const imageWrapperRef = useRef<HTMLDivElement>(null);

        const { initialized } = useAlgoliaAnalytics();
        const { translate } = useTranslation();
        const { trackProductClick } = useGtm();
        const { activeMarket } = useFranchise();

        // Use this to determine if primary image should be loaded eagerly or lazily
        const { isInitialRender } = useIsInitialRenderState();

        // Used to track if the product card is in view
        const card = useRef<HTMLAnchorElement | null>(null);
        const inView = useInView(card, { once: true, threshold: 0.1 });

        // Function to handle card click
        const handleCardClick = useCallback(() => {
            trackProductClick(gtmProductMapper([product]), listType, activeMarket?.currency);

            // Algolia Tracking
            // using insights function Algolia will infer important parameters such as:
            // index, positions, and objectIDs
            if (insights && initialized) {
                insights('clickedObjectIDsAfterSearch', {
                    eventName: 'Product Clicked',
                });
            }
        }, [activeMarket?.currency, initialized, insights, listType, product, trackProductClick]);

        const { images } = product || {};
        const [primaryImage, secondaryImage] = images || [];

        // Function to set the active slide for mobile screens
        const handleScroll = useCallback(() => {
            const imageWrapper = imageWrapperRef.current;
            if (!imageWrapper) return;
            setActiveSlide(imageWrapper.scrollLeft >= imageWrapper.clientWidth / 2 ? 1 : 0);
        }, []);

        const detailUrl = !queryId
            ? product.productUrl
            : `${product.productUrl}#queryId=${queryId}`;

        return (
            <CMSLink
                onClick={handleCardClick}
                href={detailUrl}
                className={clsx(styles.root, 'hover-effect-underline-center')}
                ref={card}
            >
                <div className={styles.imageRoot}>
                    <div
                        className={clsx(styles.imageWrapper, {
                            [styles.hasSecondaryImage]: secondaryImage,
                        })}
                        ref={imageWrapperRef}
                        onScroll={isMobile && secondaryImage ? handleScroll : undefined}
                    >
                        <ProductImage
                            className={styles.primaryImageWrapper}
                            image={primaryImage}
                            priority={isInitialRender && index !== undefined && index < 4}
                            sizes={sizes || fallBackImageSizes}
                        />

                        {secondaryImage && !(isSlider && isMobile) ? (
                            <ProductImage
                                className={styles.secondaryImageWrapper}
                                image={secondaryImage}
                                loading="lazy"
                                sizes={sizes || fallBackImageSizes}
                            />
                        ) : null}
                    </div>
                    <div className={styles.tagsTop}>
                        <ProductTagsLegacy
                            productTags={product.imageTags}
                            layout="list"
                            selectFrom="top"
                        />
                        <ProductTagsLegacy
                            productTags={product.imageTags}
                            selectFrom="top"
                            isCampaign
                        />
                    </div>
                    {/* Dots */}
                    {!isSlider && secondaryImage && (
                        <div className={styles.dots}>
                            {[...Array(2)].map((_, index) => (
                                <div
                                    key={index}
                                    className={clsx(styles.dot, {
                                        [styles.active]: activeSlide === index,
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.productDetails}>
                    {product.brand ? (
                        <Typography className={styles.brand} variant="bodySm">
                            {product.brand}
                        </Typography>
                    ) : null}
                    <div className={styles.detailsTop}>
                        <div className={styles.namesWrapper}>
                            <Typography className={styles.name} variant="body">
                                {product.name}
                            </Typography>
                            <Typography className={styles.subtitle} variant="caption" as="p">
                                {product.subtitle}
                            </Typography>
                        </div>
                        {product.colors ? (
                            <Typography className={styles.colorsPC} variant="bodySm">
                                {product.colors} {translate('commerce.colors')}
                            </Typography>
                        ) : null}
                    </div>
                    <div className={styles.detailsBottom}>
                        {product.priceDetails ? <PriceGroup {...product.priceDetails} /> : null}
                        <ProductTagsLegacy
                            productTags={product.imageTags}
                            layout="list"
                            selectFrom="bottom"
                        />
                        <Arrow className={styles.arrow} />
                    </div>
                    {product.colors ? (
                        <Typography className={styles.colorsMobile} variant="caption" as="p">
                            {product.colors} {translate('commerce.colors')}
                        </Typography>
                    ) : null}
                </div>
                <ProductImpressionTracker inView={inView} product={product} />
            </CMSLink>
        );
    }
);

export default ProductCard;
